import React, { useState, useEffect } from 'react'

const GenerateCertificateForm = ({
  initialData,
  onSubmit,
  loading,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    student_id: '',
    student_name: '',
    enrollment_number: '',
    course: '',
    college_name: '',
    technology: '',
    certificate_id: '', // Auto-generate a unique ID
    authorized_signature: '',
    joining_date: '',
    completion_date: '',
  })

  useEffect(() => {
    if (initialData) {
      // Function to format date to YYYY-MM-DD for input type="date"
      const formatDate = (dateString) => {
        if (!dateString) return ''
        try {
          // Create a new Date object and format it
          const date = new Date(dateString)
          return date.toISOString().split('T')[0]
        } catch (e) {
          console.error('Invalid date format:', dateString)
          return ''
        }
      }

      setFormData({
        student_id: String(initialData.id) || '', // Convert to string
        student_name: initialData.name || '',
        enrollment_number: initialData.enrollment_number || '',
        college_name: initialData.school_or_college_name || '', // Assumes this field might exist on the student object
        technology: initialData.technologies || '',
        course: initialData.course || '',
        certificate_id: '',
        authorized_signature: '', // Default signatory
        joining_date: formatDate(initialData.registration_date) || '',
        completion_date: formatDate(new Date()), // Default completion to today's date
      })
    }
  }, [initialData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Ensure student_id is string before sending
    const submitData = {
      ...formData,
      student_id: String(formData.student_id),
    }
    // Pass the collected form data to the parent component's handler
    onSubmit(submitData)
  }

  const inputStyle =
    'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
  const labelStyle = 'block text-sm font-medium text-gray-700 mb-1'

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* student_id is hidden but required for the API call */}
        <input type='hidden' name='student_id' value={formData.student_id} />
        <div>
          <label htmlFor='student_name' className={labelStyle}>
            Student Name
          </label>
          <input
            type='text'
            id='student_name'
            name='student_name'
            value={formData.student_name}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
        <div>
          <label htmlFor='enrollment_number' className={labelStyle}>
            Enrollment No.
          </label>
          <input
            type='text'
            id='enrollment_number'
            name='enrollment_number'
            value={formData.enrollment_number}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
        <div>
          <label htmlFor='college_name' className={labelStyle}>
            College Name
          </label>
          <input
            type='text'
            id='college_name'
            name='college_name'
            placeholder='e.g., Institute of Technology'
            value={formData.college_name}
            onChange={handleChange}
            className={inputStyle}
          />
        </div>{' '}
        <div>
          <label htmlFor='college_name' className={labelStyle}>
            course
          </label>
          <input
            type='text'
            id='course'
            name='course'
            placeholder='e.g., Institute of Technology'
            value={formData.course}
            onChange={handleChange}
            className={inputStyle}
          />
        </div>
        <div>
          <label htmlFor='technology' className={labelStyle}>
            Technologies
          </label>
          <input
            type='text'
            id='technology'
            name='technology'
            value={formData.technology}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
        <div>
          <label htmlFor='certificate_id' className={labelStyle}>
            Certificate ID
          </label>
          <input
            type='text'
            id='certificate_id'
            name='certificate_id'
            value={formData.certificate_id}
            onChange={handleChange}
            className={`${inputStyle} bg-gray-100`}
          />
          <p className='text-xs text-gray-500 mt-1'>
            specific generated certificate ID
          </p>
        </div>
        <div>
          <label htmlFor='authorized_signature' className={labelStyle}>
            Authorized Signatory
          </label>
          <input
            type='text'
            id='authorized_signature'
            name='authorized_signature'
            value={formData.authorized_signature}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
        <div>
          <label htmlFor='joining_date' className={labelStyle}>
            Joining Date
          </label>
          <input
            type='date'
            id='joining_date'
            name='joining_date'
            value={formData.joining_date}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
        <div>
          <label htmlFor='completion_date' className={labelStyle}>
            Completion Date
          </label>
          <input
            type='date'
            id='completion_date'
            name='completion_date'
            value={formData.completion_date}
            onChange={handleChange}
            className={inputStyle}
            required
          />
        </div>
      </div>

      <div className='flex justify-end space-x-4 pt-4 border-t border-gray-200'>
        <button
          type='button'
          onClick={onCancel}
          disabled={loading}
          className='px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={loading}
          className='px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed flex items-center transition-colors'
        >
          {loading && (
            <svg
              className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
          )}
          {loading ? 'Generating...' : 'Generate'}
        </button>
      </div>
    </form>
  )
}

export default GenerateCertificateForm
